import { Link } from "react-router-dom";
import { Star, Phone, Quote, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { PHONE, PHONE_LINK, BUSINESS_NAME, SOCIAL_PROFILES } from "@/lib/constants";
import { reviewsData, displayReviews } from "@/lib/reviews";

const GBP_URL = SOCIAL_PROFILES.find((u) => u.includes("google.com/maps")) ??
  "https://www.google.com/search?q=Tru+American+Plumbing+Purifications";

const Reviews = () => {
  const baseUrl = "https://truamericanplumbing.com";
  const hasReviews = displayReviews.length > 0;
  const ratingValue = reviewsData.rating ? reviewsData.rating.toFixed(1) : "5.0";
  const totalReviews = reviewsData.totalReviews || displayReviews.length;

  const reviewSchema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
        { "@type": "ListItem", position: 2, name: "Reviews", item: `${baseUrl}/reviews` },
      ],
    },
    ...(totalReviews > 0
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Plumber",
            "@id": `${baseUrl}/#organization`,
            name: BUSINESS_NAME,
            sameAs: SOCIAL_PROFILES,
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue,
              reviewCount: String(totalReviews),
              bestRating: "5",
              worstRating: "1",
            },
            review: displayReviews.slice(0, 20).map((r) => ({
              "@type": "Review",
              author: { "@type": "Person", name: r.reviewer.displayName },
              datePublished: r.createTime?.slice(0, 10),
              reviewRating: {
                "@type": "Rating",
                ratingValue: String(r.starRating),
                bestRating: "5",
              },
              reviewBody: r.comment,
            })),
          },
        ]
      : []),
  ];

  return (
    <>
      <SEO
        title="Customer Reviews - See What Our Clients Say"
        description="Read verified Google reviews from satisfied customers of Tru American Plumbing Purifications in Tolleson and the West Valley."
        canonicalUrl="/reviews/"
        schema={reviewSchema}
      />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 to-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Customer <span className="text-gradient-copper">Reviews</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Verified Google reviews from our neighbors throughout the West Valley.
            </p>
            <div className="flex items-center justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-8 w-8 ${
                    star <= Math.round(Number(ratingValue))
                      ? "text-primary fill-primary"
                      : "text-muted"
                  }`}
                />
              ))}
            </div>
            <p className="text-muted-foreground">
              {ratingValue} average{totalReviews ? ` · ${totalReviews} Google reviews` : ""}
            </p>
            <a
              href={GBP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-3"
            >
              See all reviews on Google <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-16 md:py-24">
        <div className="container">
          {hasReviews ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayReviews.map((review) => (
                <div
                  key={review.reviewId}
                  className="bg-card border border-border rounded-xl p-6 hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= review.starRating
                            ? "text-primary fill-primary"
                            : "text-muted"
                        }`}
                      />
                    ))}
                  </div>
                  <Quote className="h-8 w-8 text-primary/30 mb-3" />
                  <p className="text-foreground/80 mb-4 leading-relaxed whitespace-pre-line">
                    {review.comment}
                  </p>
                  <div className="pt-4 border-t border-border flex items-center gap-3">
                    {review.reviewer.profilePhotoUrl && (
                      <img
                        src={review.reviewer.profilePhotoUrl}
                        alt={review.reviewer.displayName}
                        loading="lazy"
                        className="h-10 w-10 rounded-full"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-foreground truncate">
                        {review.reviewer.displayName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Verified Google review{review.relativeTime ? ` · ${review.relativeTime}` : ""}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="max-w-xl mx-auto text-center bg-card border border-border rounded-xl p-10">
              <p className="text-foreground/80 mb-6">
                Our latest Google reviews are loading. In the meantime, you can read
                every verified review directly on our Google Business Profile.
              </p>
              <a href={GBP_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="hero" size="lg" className="gap-2">
                  Read reviews on Google <ExternalLink className="h-4 w-4" />
                </Button>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-primary/20 via-background to-background">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Experience 5-Star Service?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Join our satisfied customers throughout the West Valley. Contact us
            today for professional plumbing service you can trust.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PHONE_LINK}>
              <Button variant="hero" size="lg" className="gap-2">
                <Phone className="h-4 w-4" />
                {PHONE}
              </Button>
            </a>
            <Link to="/contact/">
              <Button variant="outline" size="lg">
                Request Service
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Reviews;
